import { memo, useCallback, useState } from "react";
import { Button, Div, filterHover, Image, Text, useTheme } from "react-better-html";

import {
   useFirebaseUserProfileImageExists,
   useProfileImage,
   useReduxAction,
   useReduxState,
} from "../../../utils/hooks";
import { useI18n } from "../../../utils/i18n";

import Section from "../../../components/structure/Section";

const profileImageSize = 100;

function AccountSection() {
   const theme = useTheme();
   const { t } = useI18n();
   const profileImage = useProfileImage();
   const { fileExists: hasProfileImage, setFileExists: setHasProfileImage } = useFirebaseUserProfileImageExists();

   const firebaseUser = useReduxState((state) => state.user.firebaseUser);

   const editFirebaseUserProfileImage = useReduxAction((actions) => actions.user.editFirebaseUserProfileImage);

   const [uploadedProfileImageFile, setUploadedProfileImageFile] = useState<File>();

   const onClickEditProfileImage = useCallback(() => {
      const input = document.createElement("input");
      input.setAttribute("type", "file");
      input.setAttribute("accept", "image/*");

      input.addEventListener("change", () => {
         const uploadedFile = input.files?.[0];

         setUploadedProfileImageFile(uploadedFile);

         editFirebaseUserProfileImage({
            file: uploadedFile,
            onSuccess: () => {
               setUploadedProfileImageFile(undefined);
               setHasProfileImage.setTrue();
            },
            onError: () => {
               setUploadedProfileImageFile(undefined);
            },
         });
      });

      input.click();
   }, []);
   const onClickRemoveProfileImage = useCallback(() => {
      editFirebaseUserProfileImage({
         file: undefined,
         onSuccess: () => {
            setHasProfileImage.setFalse();
         },
      });
   }, []);

   return firebaseUser ? (
      <Section>
         <Div.row alignItems="center" gap={theme.styles.space}>
            <Div width={profileImageSize} height={profileImageSize} position="relative">
               <Image.profileImage
                  src={uploadedProfileImageFile ? URL.createObjectURL(uploadedProfileImageFile) : profileImage}
                  size={profileImageSize}
               />

               <Button.icon
                  icon="pen"
                  position="absolute"
                  bottom={0}
                  right={0}
                  color={theme.colors.base}
                  backgroundColor={theme.colors.textPrimary}
                  backgroundColorHover={theme.colors.textPrimary}
                  filterHover={filterHover().z1}
                  border={`solid 2px ${theme.colors.backgroundContent}`}
                  loaderName="user:editFirebaseUserProfileImage"
                  onClick={onClickEditProfileImage}
               />
            </Div>

            <Div.column flex={1} gap={theme.styles.gap / 2}>
               <Text fontSize={20} fontWeight={700}>
                  {firebaseUser.displayName}
               </Text>
               <Text color={theme.colors.textSecondary}>{firebaseUser.email}</Text>
               <Text fontSize={14} color={theme.colors.textSecondary}>
                  {t((l) => l.pages.account.sections.account.memberSince)}
               </Text>
            </Div.column>

            {hasProfileImage && (
               <Button.secondary
                  text={t((l) => l.pages.account.sections.account.profileImage.remove)}
                  isSmall
                  backgroundColor={theme.colors.backgroundBase}
                  loaderName="user:editFirebaseUserProfileImage"
                  onClick={onClickRemoveProfileImage}
               />
            )}
         </Div.row>
      </Section>
   ) : undefined;
}

export default memo(AccountSection);
